/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useState, useRef } from 'react'
import {
  useChannel,
  useMerchandisingURL,
  ChannelHelper,
  UserHelper,
  KeycloakHelper,
} from '@monorepo/infra'

import { Merchandise } from '@monorepo/interfaces'
import { useKeycloak } from '@react-keycloak/web'

const securityProfileBaseUrl = new URL(
  `${process.env.REACT_APP_TX_BASE_URL}/Security/Profile`
)

export interface SecurityProfile {
  ErrorOccured: boolean
  UPN: string
  FirstName: string
  LastName: string
  PhoneNumber: string
  Address: string
  City: string
  State: string
  ZipCode: string
  MasterDealerName: string
  MasterDealerId: string
  CorpId: string
  DealerInformations: DealerInformation[]
}

export interface DealerInformation {
  ErrorOccured: boolean
  MasterDealerId: string
  UPN: string
  DealerCode: string
  DealerChannel: string
  PreferredDealer: boolean
  LocationName: string
  PhoneNumber: string
  Address: string
  City: string
  State: string
  ZipCode: string
}

interface UseMerchandiseURL {
  merchandiseURL: string
  error: string | null | undefined
}

export const useMerchandiseURL = (): UseMerchandiseURL => {
  const channel = useChannel()
  const { selectedChannel, selectedMasterDealerId, groups } = channel
  const { keycloak } = useKeycloak()
  const user = KeycloakHelper.getTokenParsed(keycloak)

  const [merchandiseURL, setMerchandiseURL] = useState('')

  const prevSelectedChannel = useRef<string | null | undefined>()
  const prevSelectedMasterDealerId = useRef<string | null | undefined>()
  const isChannelContextChanged = (
    selectedChannel: string | null | undefined,
    selectedMasterDealerId: string | null | undefined
  ): boolean =>
    prevSelectedChannel.current !== selectedChannel ||
    prevSelectedMasterDealerId.current !== selectedMasterDealerId
  const [error, setError] = useState<string | null | undefined>(undefined)

  const getMerchandiseUrl = useCallback(
    async (principalEmail: string, securityProfile: SecurityProfile) => {
      if (!channel) return

      if (
        !selectedChannel ||
        !selectedMasterDealerId ||
        (merchandiseURL &&
          !isChannelContextChanged(selectedChannel, selectedMasterDealerId))
      )
        return

      const selectedDealerInformations =
        securityProfile?.DealerInformations?.find(
          (di) =>
            di.DealerChannel === selectedChannel &&
            di.MasterDealerId ===
              selectedMasterDealerId + selectedChannel.charAt(0)
        )

      // Use 9999999999L for all PV (2252), NMG (3550), and DSI (2251) dealers who are not dealerAdmins
      const dto: Merchandise = {
        channel: channel.selectedChannel,
        subdealer:
          channel.hasCoop &&
          !ChannelHelper.canSeeCoop(
            channel.selectedMasterDealerId,
            channel.selectedChannel,
            UserHelper.isImpersonatedUserDealerAdmin(keycloak) ||
              UserHelper.isDealerAdmin(groups)
          )
            ? 'T'
            : 'F',
        retail:
          !channel.hasCoop ||
          !ChannelHelper.canSeeCoop(
            channel.selectedMasterDealerId,
            channel.selectedChannel,
            UserHelper.isImpersonatedUserDealerAdmin(keycloak) ||
              UserHelper.isDealerAdmin(groups)
          )
            ? 'T'
            : 'F',
        dealerCode: `${selectedMasterDealerId}${channel?.selectedChannel?.charAt(
          0
        )}`,
        locationName: selectedDealerInformations?.LocationName ?? '',
        locationPhone: selectedDealerInformations?.PhoneNumber ?? '',
        masterDealerName: securityProfile?.MasterDealerName ?? '',
        principalEmail,
      }

      const merchandiseUrl = useMerchandisingURL(dto)

      if (merchandiseUrl.error) {
        return
      }

      setMerchandiseURL(merchandiseUrl.urlEncoded || '')
    },
    [channel, merchandiseURL, groups]
  )

  const getSecurityProfile = useCallback(
    async (email = '') => {
      try {
        const token = KeycloakHelper.getToken(keycloak)
        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined

        if (headers?.Authorization) {
          console.log(
            '[useMerchandiseUrl] Authorization header',
            headers.Authorization
          )
        } else {
          console.log('[useMerchandiseUrl] Authorization header missing')
        }

        const response = await fetch(`${securityProfileBaseUrl}/${email}/Get`, {
          headers,
        })

        if (!response.ok) {
          throw new Error(
            `Failed to fetch security profile: ${response.status} ${response.statusText}`
          )
        }

        const data = await response.json()
        return data as SecurityProfile
      } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
          throw new Error(
            'Failed to fetch security profile: Network error. Please check your connection.'
          )
        }
        throw err
      }
    },
    [keycloak]
  )

  useEffect(() => {
    if (!channel) return

    const { selectedChannel, selectedMasterDealerId } = channel

    if (
      merchandiseURL &&
      !isChannelContextChanged(selectedChannel, selectedMasterDealerId)
    )
      return

    const _getMerchandiseUrl = async () => {
      try {
        const securityProfile = await getSecurityProfile(user?.email)

        if (securityProfile) {
          await getMerchandiseUrl(user?.email as string, securityProfile)
        }

        prevSelectedChannel.current = selectedChannel
        prevSelectedMasterDealerId.current = selectedMasterDealerId
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : String(err)
        setError(
          `An error occurred retrieving the Merchandise URL: ${errorMessage}`
        )
      }
    }

    _getMerchandiseUrl()
  }, [getMerchandiseUrl, getSecurityProfile, keycloak, merchandiseURL, channel])

  return { merchandiseURL, error }
}
